import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Select, Card, Button, InputNumber } from 'antd';
import { useEntries } from '../context/EntriesContext';

// Custom YAxis tick component to display leader images only (no text)
const CustomYAxisTick = ({ x, y, payload, data, onLeaderClick }) => {
  const leaderData = data.find(item => item.leader === payload.value);
  const imageUrl = leaderData?.image_url;
  const imageSize = 140; // Much larger image size
  const offset = imageSize / 2; // Center the image vertically
  const leaderName = payload.value;

  const handleClick = () => {
    if (onLeaderClick && leaderName) onLeaderClick(leaderName);
  };

  return (
    <g transform={`translate(${x},${y})`}>
      {imageUrl ? (
        <foreignObject x={-imageSize - 15} y={-offset} width={imageSize} height={imageSize}>
          <div
            role="button"
            tabIndex={0}
            onClick={handleClick}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(); }}
            style={{
              width: `${imageSize}px`,
              height: `${imageSize}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              border: '2px solid #ddd',
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
              padding: '4px',
              cursor: onLeaderClick ? 'pointer' : 'default',
            }}
          >
            <img
              src={imageUrl}
              alt={leaderName}
              title={leaderName}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                borderRadius: '4px',
                pointerEvents: 'none',
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        </foreignObject>
      ) : (
        <g
          role="button"
          tabIndex={0}
          onClick={handleClick}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(); }}
          style={{ cursor: onLeaderClick ? 'pointer' : 'default' }}
        >
          <text
            x={-10}
            y={0}
            dy={3}
            textAnchor="end"
            fill="#999"
            fontSize={11}
          >
            {leaderName.split(' ')[0]}
          </text>
        </g>
      )}
    </g>
  );
};

function DeckDistributionChart() {
  const navigate = useNavigate();
  const chartWrapperRef = useRef(null);
  const { entries: allEntries, loading, error: entriesError } = useEntries();
  const [filteredData, setFilteredData] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ sets: [], tournaments: [], regions: [] });
  const [selectedSets, setSelectedSets] = useState([]);
  const [selectedTournaments, setSelectedTournaments] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [topFilter, setTopFilter] = useState(32);
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Handle window resize for responsive design
  useEffect(() => {
    // Check if window is available (client-side)
    if (typeof window !== 'undefined') {
      setIsMobile(window.innerWidth < 768);
      
      const handleResize = () => {
        setIsMobile(window.innerWidth < 768);
      };
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  // Derive filter options from cached entries
  useEffect(() => {
    if (allEntries.length === 0) {
      setFilterOptions({ sets: [], tournaments: [], regions: [] });
      return;
    }
    const sets = [...new Set(allEntries.map(e => e.set_name).filter(Boolean))].sort();
    const tournaments = [...new Set(allEntries.map(e => e.tournament).filter(Boolean))].sort();
    const regions = [...new Set(allEntries.map(e => e.region).filter(Boolean))].sort();
    setFilterOptions({ sets, tournaments, regions });
  }, [allEntries]);

  // Calculate available filter options based on current selections and auto-deselect invalid options
  useEffect(() => {
    if (allEntries.length === 0) return;

    // Build progressively filtered datasets for each filter type
    // Available sets: all sets that match current tournament/region filters
    let setsBase = allEntries;
    if (selectedTournaments.length > 0) {
      setsBase = setsBase.filter(e => selectedTournaments.includes(e.tournament));
    }
    if (selectedRegions.length > 0) {
      setsBase = setsBase.filter(e => selectedRegions.includes(e.region));
    }
    const availableSets = [...new Set(setsBase.map(e => e.set_name).filter(Boolean))].sort();
    
    // Available tournaments: all tournaments that match current set/region filters
    let tournamentsBase = allEntries;
    if (selectedSets.length > 0) {
      tournamentsBase = tournamentsBase.filter(e => selectedSets.includes(e.set_name));
    }
    if (selectedRegions.length > 0) {
      tournamentsBase = tournamentsBase.filter(e => selectedRegions.includes(e.region));
    }
    const availableTournaments = [...new Set(tournamentsBase.map(e => e.tournament).filter(Boolean))].sort();
    
    // Available regions: all regions that match current set/tournament filters
    let regionsBase = allEntries;
    if (selectedSets.length > 0) {
      regionsBase = regionsBase.filter(e => selectedSets.includes(e.set_name));
    }
    if (selectedTournaments.length > 0) {
      regionsBase = regionsBase.filter(e => selectedTournaments.includes(e.tournament));
    }
    const availableRegions = [...new Set(regionsBase.map(e => e.region).filter(Boolean))].sort();
    
    // Auto-deselect invalid selections
    const validSets = selectedSets.filter(s => availableSets.includes(s));
    const validTournaments = selectedTournaments.filter(t => availableTournaments.includes(t));
    const validRegions = selectedRegions.filter(r => availableRegions.includes(r));
    
    if (validSets.length !== selectedSets.length) {
      setSelectedSets(validSets);
    }
    if (validTournaments.length !== selectedTournaments.length) {
      setSelectedTournaments(validTournaments);
    }
    if (validRegions.length !== selectedRegions.length) {
      setSelectedRegions(validRegions);
    }
    
    // Update filter options - show all if nothing selected, otherwise show only valid options
    const allSets = [...new Set(allEntries.map(e => e.set_name).filter(Boolean))].sort();
    const allTournaments = [...new Set(allEntries.map(e => e.tournament).filter(Boolean))].sort();
    const allRegions = [...new Set(allEntries.map(e => e.region).filter(Boolean))].sort();
    
    setFilterOptions({
      sets: selectedSets.length === 0 ? allSets : availableSets,
      tournaments: selectedTournaments.length === 0 && selectedSets.length === 0 ? allTournaments : availableTournaments,
      regions: selectedRegions.length === 0 && selectedSets.length === 0 && selectedTournaments.length === 0 ? allRegions : availableRegions
    });
  }, [allEntries, selectedSets, selectedTournaments, selectedRegions]);

  // Filter and aggregate data client-side whenever filters change
  useEffect(() => {
    if (allEntries.length === 0) {
      setFilteredData([]);
      return;
    }

    // Filter entries based on selected filters
    let filtered = allEntries.filter(entry => {
      // Filter by placement (top N)
      if (entry.placement > topFilter) return false;
      
      // Filter by sets
      if (selectedSets.length > 0 && !selectedSets.includes(entry.set_name)) return false;
      
      // Filter by tournaments
      if (selectedTournaments.length > 0 && !selectedTournaments.includes(entry.tournament)) return false;
      
      // Filter by regions
      if (selectedRegions.length > 0 && !selectedRegions.includes(entry.region)) return false;
      
      return true;
    });

    // Aggregate by leader
    const leaderMap = {};
    filtered.forEach(entry => {
      if (!entry.deck) return;
      
      if (!leaderMap[entry.deck]) {
        leaderMap[entry.deck] = {
          leader_name: entry.deck,
          count: 0,
          regions: {},
          image_url: null
        };
      }
      
      leaderMap[entry.deck].count++;
      leaderMap[entry.deck].regions[entry.region] = (leaderMap[entry.deck].regions[entry.region] || 0) + 1;
    });

    // Convert to array and calculate percentages
    const total = Object.values(leaderMap).reduce((sum, item) => sum + item.count, 0);
    const distribution = Object.values(leaderMap).map(item => ({
      ...item,
      percentage: total > 0 ? (item.count / total * 100) : 0,
      image_url: getLeaderImageUrl(item.leader_name)
    }));

    // Sort by count descending
    distribution.sort((a, b) => b.count - a.count);
    
    setFilteredData(distribution);
  }, [allEntries, selectedSets, selectedTournaments, selectedRegions, topFilter]);

  // Helper function to get leader image URL (same logic as backend)
  const getLeaderImageUrl = (leaderName) => {
    if (!leaderName) return null;
    const match = leaderName.match(/^([A-Z0-9]+)-(\d+)/);
    if (!match) return null;
    const setName = match[1];
    const cardId = match[0];
    return `https://limitlesstcg.nyc3.cdn.digitaloceanspaces.com/one-piece/${setName}/${cardId}_EN.webp`;
  };

  const clearAllFilters = () => {
    setSelectedSets([]);
    setSelectedTournaments([]);
    setSelectedRegions([]);
  };

  if (loading) {
    return <div>Loading deck distribution...</div>;
  }

  if (entriesError) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#c33' }}>
        <p>Failed to load entries: {entriesError}</p>
      </div>
    );
  }

  // Check if we have any data at all
  if (allEntries.length === 0) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p style={{ fontSize: '18px', marginBottom: '10px', color: '#666' }}>
          No data available.
        </p>
        <p style={{ color: '#999', marginBottom: '15px' }}>
          The database appears to be empty. You need to import CSV files first.
        </p>
        <div style={{ 
          backgroundColor: '#f5f5f5', 
          padding: '15px', 
          borderRadius: '8px', 
          marginTop: '20px',
          textAlign: 'left',
          display: 'inline-block',
          maxWidth: '600px'
        }}>
          <p style={{ color: '#333', fontWeight: '600', marginBottom: '10px' }}>
            To import data:
          </p>
          <ol style={{ color: '#666', marginLeft: '20px', lineHeight: '1.8' }}>
            <li>Make sure your CSV files are in the <code>data/</code> directory</li>
            <li>Call the API endpoint: <code>POST http://localhost:8000/api/admin/refresh-data</code></li>
            <li>You can use curl, Postman, or the FastAPI docs at <code>http://localhost:8000/docs</code></li>
          </ol>
          <p style={{ color: '#999', fontSize: '12px', marginTop: '15px' }}>
            Expected CSV location: <code>data/OP13/OP13 Meta Data.csv</code> (or similar for other sets)
          </p>
        </div>
      </div>
    );
  }

  // Check if filters result in no data
  if (filteredData.length === 0) {
    const hasFilters = selectedSets.length > 0 || selectedTournaments.length > 0 || selectedRegions.length > 0;
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        {hasFilters ? (
          <>
            <p style={{ fontSize: '18px', marginBottom: '10px', color: '#666' }}>
              No data matches your current filters.
            </p>
            <p style={{ color: '#999' }}>
              Try adjusting your filter selections or clearing filters to see all data.
            </p>
            <Button type="primary" size="large" onClick={clearAllFilters} style={{ marginTop: 20 }}>
              Clear all filters
            </Button>
          </>
        ) : (
          <>
            <p style={{ fontSize: '18px', marginBottom: '10px', color: '#666' }}>
              No deck data available after filtering.
            </p>
            <p style={{ color: '#999' }}>
              All entries may be missing deck information. Check your CSV files.
            </p>
          </>
        )}
      </div>
    );
  }

  // Prepare data for chart (show all filtered results)
  const chartData = filteredData.map(item => ({
    leader: item.leader_name,
    count: item.count,
    percentage: item.percentage.toFixed(1),
    image_url: item.image_url
  }));

  const hasActiveFilters = selectedSets.length > 0 || selectedTournaments.length > 0 || selectedRegions.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '24px', height: '100%', flex: 1 }}>
      {/* Mobile Filter Toggle */}
      {isMobile && (
        <Button
          type="primary"
          size="large"
          onClick={() => setShowFiltersMobile(!showFiltersMobile)}
          block
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <span>
            Filters {hasActiveFilters && `(${selectedSets.length + selectedTournaments.length + selectedRegions.length} selected)`}</span>
          <span>{showFiltersMobile ? '▲ Hide' : '▼ Show'}</span>
        </Button>
      )}

      {/* Sidebar Filters */}
      {(!isMobile || showFiltersMobile) && (
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <span>Filters</span>
              {hasActiveFilters && (
                <Button type="link" danger size="small" onClick={clearAllFilters} style={{ padding: 0, height: 'auto' }}>
                  Clear all
                </Button>
              )}
            </div>
          }
          style={{
            width: isMobile ? '100%' : 300,
            flexShrink: 0,
            position: isMobile ? 'relative' : 'sticky',
            top: isMobile ? 'auto' : 20,
            marginBottom: isMobile ? 16 : 0,
          }}
          styles={{ body: { paddingTop: 8 } }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <div style={{ marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#333' }}>Format (set)</div>
              <Select
                mode="multiple"
                placeholder="All formats"
                value={selectedSets}
                onChange={setSelectedSets}
                options={filterOptions.sets.map(s => ({ label: s, value: s }))}
                allowClear
                style={{ width: '100%' }}
                maxTagCount="responsive"
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </div>
            <div>
              <div style={{ marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#333' }}>Tournament</div>
              <Select
                mode="multiple"
                placeholder="All tournaments"
                value={selectedTournaments}
                onChange={setSelectedTournaments}
                options={filterOptions.tournaments.map(t => ({ label: t, value: t }))}
                allowClear
                style={{ width: '100%' }}
                maxTagCount="responsive"
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </div>
            <div>
              <div style={{ marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#333' }}>Region</div>
              <Select
                mode="multiple"
                placeholder="All regions"
                value={selectedRegions}
                onChange={setSelectedRegions}
                options={filterOptions.regions.map(r => ({ label: r, value: r }))}
                allowClear
                style={{ width: '100%' }}
                maxTagCount="responsive"
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </div>
            <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
              <div style={{ marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#333' }}>Top placement</div>
              <InputNumber
                min={1}
                value={topFilter}
                onChange={(v) => setTopFilter(v && v >= 1 ? v : 32)}
                style={{ width: '100%' }}
              />
              <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                Show decks with placement ≤ {topFilter}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Main Chart Area */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <div
          ref={chartWrapperRef}
          style={{
            flex: 1,
            height: isMobile ? '500px' : 'calc(100vh - 180px)',
            minHeight: '500px',
            overflowY: 'auto',
            overflowX: 'hidden',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: '#fff'
          }}
        >
          <ResponsiveContainer width="100%" height={Math.max(isMobile ? 500 : (typeof window !== 'undefined' ? window.innerHeight - 180 : 600), chartData.length * 150 + 100)}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 20, right: 60, left: 170, bottom: 20 }}
              barCategoryGap="20%"
              onClick={(state) => {
                const payload = state?.activePayload?.[0]?.payload;
                const leader = payload?.leader ?? state?.activeLabel;
                if (leader) navigate(`/decks/${encodeURIComponent(leader)}`);
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis
                dataKey="leader"
                type="category"
                width={160}
                tick={(props) => <CustomYAxisTick {...props} data={chartData} onLeaderClick={(leader) => navigate(`/decks/${encodeURIComponent(leader)}`)} />}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                formatter={(value, name) => {
                  if (name === 'count') return [`${value} appearances`, 'Count'];
                  if (name === 'percentage') return [`${value}%`, 'Percentage'];
                  return [value, name];
                }}
                labelFormatter={(label) => `Leader: ${label}`}
                contentStyle={{ cursor: 'pointer' }}
                labelStyle={{ marginBottom: '4px' }}
              />
              <Legend />
              <Bar 
                dataKey="count" 
                fill="#8884d8" 
                name="Appearances"
                radius={[0, 4, 4, 0]}
                cursor="pointer"
                label={{
                  position: 'right',
                  formatter: (value) => value,
                  style: { fill: '#333', fontSize: '16px', fontWeight: '700' },
                  offset: 10
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
          Showing {filteredData.length} unique leader{filteredData.length !== 1 ? 's' : ''}.
          {chartData.length > 10 && (
            <span style={{ marginLeft: '8px', color: '#999' }}>
              (Scroll to see all)
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default DeckDistributionChart;
